import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/services", label: "Nos services" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

// Liste des mots-clés et leurs pages de destination
const KEYWORDS = [
  { keyword: "Nettoyage", url: "/services#nettoyage" },
  { keyword: "Piscine", url: "/services#piscine" },
  { keyword: "Climatisation", url: "/services#climatisation" },
  { keyword: "Dératisation", url: "/services#deratisation" },
  { keyword: "Application mobile", url: "/services#app-mobile" },
  { keyword: "Site web", url: "/services#site-web" },
  { keyword: "Cybersécurité", url: "/services#cybersecurite" },
  { keyword: "Équipement IT", url: "/services#equipement-it" },
  { keyword: "Formation IA", url: "/services#formation-ia" },
  { keyword: "Cloud", url: "/services#cloud" },
  { keyword: "Devis", url: "/devis" },
  { keyword: "Contact", url: "/contact" },
  { keyword: "À propos", url: "/a-propos" },
  { keyword: "PureProCleaners", url: "https://www.pureprocleaners.ci" },
  { keyword: "PureProTechnologie", url: "https://www.pureprotechnologie.ci" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setShowModal(false); // Fermer le modal lors d'un changement de page
  }, [pathname]);

  const isHome = pathname === "/";
  const transparent = isHome && !scrolled && !open;

  // Filtrer les mots-clés selon la saisie
  const filteredKeywords = KEYWORDS.filter(({ keyword }) =>
    keyword.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Gérer l'ouverture du modal (clic sur loupe ou soumission)
  const handleSearchClick = (e) => {
    e.preventDefault();
    if (searchQuery.trim() === "") {
      // Si champ vide, afficher tous les mots-clés
      setShowModal(true);
    } else if (filteredKeywords.length === 1) {
      // Si un seul résultat, rediriger directement
      redirectTo(filteredKeywords[0].url);
    } else {
      setShowModal(true);
    }
  };

  // Rediriger vers l'URL (interne ou externe)
  const redirectTo = (url) => {
    if (url.startsWith("http")) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
    setShowModal(false);
    setSearchQuery("");
  };

  // Fermer le modal en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showModal && !e.target.closest(".search-modal") && !e.target.closest(".navbar-search") && !e.target.closest(".mobile-search")) {
        setShowModal(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showModal]);

  return (
    <>
      {/* Styles responsives intégrés + styles du modal */}
      <style>{`
        .navbar-root {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.3s ease;
        }
        .navbar-inner {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .navbar-logo {
          display: flex;
          align-items: center;
          text-decoration: none;
        }
        .navbar-logo-image {
          height: 50px;
          width: auto;
          max-width: 100%;
          object-fit: contain;
          display: block;
        }
        .navbar-links {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .navbar-link {
          padding: 6px 14px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.3px;
          text-decoration: none;
          transition: all 0.2s;
          padding-bottom: 4px;
        }
        .navbar-cta {
          display: block;
        }
        /* Styles de la recherche desktop */
        .navbar-search {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.1);
          border-radius: 40px;
          padding: 2px 2px 2px 12px;
          margin: 0 8px;
        }
        .navbar-search input {
          background: transparent;
          border: none;
          color: white;
          font-size: 13px;
          padding: 6px 0;
          outline: none;
          width: 160px;
        }
        .navbar-search input::placeholder {
          color: rgba(255,255,255,0.6);
        }
        .navbar-search button {
          background: rgba(201,162,39,0.2);
          border: none;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }
        .navbar-search button:hover {
          background: rgba(201,162,39,0.5);
        }
        /* Modal de suggestions (desktop et mobile) */
        .search-modal {
          position: absolute;
          top: 50px;
          left: 0;
          right: 0;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.2);
          z-index: 1001;
          max-height: 300px;
          overflow-y: auto;
          width: 260px;
        }
        .search-modal.mobile {
          position: static;
          width: 100%;
          margin-top: 8px;
          background: rgba(10,37,64,0.95);
          border: 1px solid rgba(255,255,255,0.2);
        }
        .search-modal a {
          display: block;
          padding: 12px 16px;
          color: #0a2540;
          text-decoration: none;
          font-size: 14px;
          border-bottom: 1px solid #eef1f8;
          transition: background 0.2s;
        }
        .search-modal.mobile a {
          color: white;
          border-bottom-color: rgba(255,255,255,0.1);
        }
        .search-modal a:hover {
          background: #f8f9fc;
        }
        .search-modal.mobile a:hover {
          background: rgba(255,255,255,0.1);
        }
        .search-modal .no-result {
          padding: 12px 16px;
          color: #8a9aaa;
          font-size: 13px;
          text-align: center;
        }
        .navbar-burger {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
        }
        .burger-line {
          width: 24px;
          height: 2px;
          background: #fff;
          border-radius: 2px;
          transition: all 0.3s;
          display: block;
        }
        .navbar-mobile-menu {
          padding: 16px 24px 24px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          border-top: 1px solid rgba(255,255,255,0.1);
          position: relative;
        }
        .navbar-mobile-link {
          padding: 12px 16px;
          font-size: 15px;
          font-weight: 600;
          border-radius: 8px;
          text-decoration: none;
        }
        /* Recherche mobile */
        .mobile-search {
          position: relative;
          margin: 8px 0 12px;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.1);
          border-radius: 40px;
          padding: 4px 4px 4px 16px;
        }
        .mobile-search input {
          flex: 1;
          background: transparent;
          border: none;
          color: white;
          font-size: 14px;
          padding: 10px 0;
          outline: none;
        }
        .mobile-search input::placeholder {
          color: rgba(255,255,255,0.6);
        }
        .mobile-search button {
          background: rgba(201,162,39,0.2);
          border: none;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 16px;
        }
        /* Responsive */
        @media (max-width: 768px) {
          .navbar-links {
            display: none !important;
          }
          .navbar-cta {
            display: none !important;
          }
          .navbar-search {
            display: none !important;
          }
          .navbar-burger {
            display: flex !important;
          }
        }
        @media (max-width: 480px) {
          .navbar-inner {
            padding: 0 16px;
          }
          .navbar-logo-image {
            height: 42px;
          }
        }
      `}</style>

      <nav
        className="navbar-root"
        style={{
          background: transparent ? "transparent" : "rgba(10,37,64,.97)",
          backdropFilter: transparent ? "none" : "blur(12px)",
          boxShadow: transparent ? "none" : "0 2px 24px rgba(10,37,64,.25)",
          borderBottom: transparent
            ? "1px solid rgba(255,255,255,.1)"
            : "1px solid rgba(255,255,255,.05)",
        }}
      >
        <div className="navbar-inner">
          {/* Logo */}
          <Link to="/" className="navbar-logo">
            <img src="/logo12 copie.png" alt="PurePro Group" className="navbar-logo-image" />
          </Link>

          {/* Desktop nav */}
          <div className="navbar-links">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="navbar-link"
                style={{
                  color: pathname === l.to ? "#c9a227" : "rgba(255,255,255,.85)",
                  borderBottom:
                    pathname === l.to ? "2px solid #c9a227" : "2px solid transparent",
                }}
              >
                {l.label}
              </Link>
            ))}
          </div>

          {/* Barre de recherche desktop avec modal */}
          <div className="navbar-search">
            <form onSubmit={handleSearchClick} style={{ display: "flex", alignItems: "center", width: "100%" }}>
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value.trim() !== "") setShowModal(true);
                  else setShowModal(false);
                }}
                onFocus={() => setShowModal(true)}
              />
              <button type="submit" aria-label="Rechercher">
                🔍
              </button>
            </form>
            {showModal && (
              <div className="search-modal">
                {filteredKeywords.length > 0 ? (
                  filteredKeywords.map(({ keyword, url }) => (
                    <a
                      key={keyword}
                      href={url}
                      onClick={(e) => {
                        e.preventDefault();
                        redirectTo(url);
                      }}
                    >
                      {keyword}
                    </a>
                  ))
                ) : (
                  <div className="no-result">Aucun résultat pour "{searchQuery}"</div>
                )}
              </div>
            )}
          </div>

          {/* CTA Desktop */}
          <div className="navbar-cta">
            <Link
              to="/devis"
              className="btn btn-primary"
              style={{ padding: "10px 22px", fontSize: "13px" }}
            >
              Devis gratuit →
            </Link>
          </div>

          {/* Hamburger button */}
          <button
            onClick={() => setOpen(!open)}
            className="navbar-burger"
            aria-label="Menu"
          >
            <span
              className="burger-line"
              style={{ transform: open ? "rotate(45deg) translateY(7px)" : "none" }}
            />
            <span
              className="burger-line"
              style={{ opacity: open ? 0 : 1 }}
            />
            <span
              className="burger-line"
              style={{ transform: open ? "rotate(-45deg) translateY(-7px)" : "none" }}
            />
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="navbar-mobile-menu">
            {/* Recherche mobile avec modal intégrée */}
            <div className="mobile-search">
              <form onSubmit={handleSearchClick} style={{ display: "flex", flex: 1 }}>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (e.target.value.trim() !== "") setShowModal(true);
                    else setShowModal(false);
                  }}
                  onFocus={() => setShowModal(true)}
                />
                <button type="submit" aria-label="Rechercher">
                  🔍
                </button>
              </form>
            </div>
            {showModal && (
              <div className="search-modal mobile">
                {filteredKeywords.length > 0 ? (
                  filteredKeywords.map(({ keyword, url }) => (
                    <a
                      key={keyword}
                      href={url}
                      onClick={(e) => {
                        e.preventDefault();
                        redirectTo(url);
                      }}
                    >
                      {keyword}
                    </a>
                  ))
                ) : (
                  <div className="no-result">Aucun résultat pour "{searchQuery}"</div>
                )}
              </div>
            )}

            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="navbar-mobile-link"
                style={{
                  color: pathname === l.to ? "#c9a227" : "rgba(255,255,255,.9)",
                }}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/devis"
              className="btn btn-primary"
              style={{ marginTop: "12px", justifyContent: "center", textAlign: "center" }}
            >
              Devis gratuit →
            </Link>
          </div>
        )}
      </nav>
    </>
  );
}